'use client';

import { Modal as HeroModal, type ModalProps as HeroModalProps } from '@heroui/react';
import type { ReactNode } from 'react';

import { useEscapeKey } from '@/hooks/useEscapeKey';

export interface ModalProps extends Omit<HeroModalProps, 'children'> {
  children: ReactNode;
  /**
   * ESC 키로 모달을 닫을 수 있는지 여부
   * @default true
   */
  enableEscapeKey?: boolean;
}

/**
 * HeroUI Modal을 래핑한 기본 Modal 컴포넌트
 * ESC 키로 모달을 닫을 수 있는 기능을 제공합니다.
 * 
 * @example
 * ```tsx
 * <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
 *   <ModalContent>
 *     <ModalHeader>제목</ModalHeader>
 *     <ModalBody>내용</ModalBody>
 *     <ModalFooter>푸터</ModalFooter>
 *   </ModalContent>
 * </Modal>
 * ```
 * 
 * @example
 * ESC 키 비활성화
 * ```tsx
 * <Modal isOpen={isOpen} onOpenChange={setIsOpen} enableEscapeKey={false}>
 *   <ModalContent>내용</ModalContent>
 * </Modal>
 * ```
 */
export function Modal({ children, enableEscapeKey = true, isOpen, onOpenChange, classNames, ...props }: ModalProps) {
  // ESC 키로 모달 닫기
  // HeroUI Modal의 기본 ESC 키 동작과 중복되지 않도록 useEscapeKey만 사용
  useEscapeKey(
    () => {
      if (onOpenChange && isOpen) {
        onOpenChange(false);
      }
    },
    enableEscapeKey && !!isOpen
  );

  // 기본 애니메이션 클래스와 사용자 제공 클래스 병합
  // CSS transition과 transform을 사용하여 페이드 + 스케일 애니메이션 구현
  // HeroUI Modal은 내부적으로 framer-motion을 사용하므로, 
  // CSS transition으로 추가 애니메이션 효과 제공
  const mergedClassNames = {
    base: 'transition-all duration-200 ease-out data-[entering=true]:opacity-0 data-[entering=true]:scale-95 data-[open=true]:opacity-100 data-[open=true]:scale-100 data-[exiting=true]:opacity-0 data-[exiting=true]:scale-95',
    backdrop: 'transition-opacity duration-200 ease-out',
    wrapper: 'transition-opacity duration-200',
    ...classNames,
  };

  return (
    <HeroModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={enableEscapeKey}
      classNames={mergedClassNames}
      {...props}
    >
      {children}
    </HeroModal>
  );
}
